/**
 * React Query hooks for the staff account-management screens
 * (Publishers / Admins / Super Creators + detail). Fetchers + types live
 * in `@/api/adminUsers`; this file wires caching/invalidation + success
 * toasts consistent with the rest of the admin console.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AdminUser,
  ListUsersParams,
  UpdateUserInput,
  approveUser,
  fetchUser,
  listSuperCreatorPublishers,
  listUsers,
  rejectUser,
  setPrimaryUser,
  updateUser
} from '@/api/adminUsers';

export function useUsers(params: ListUsersParams) {
  return useQuery({
    queryKey: ['adminUsers', params],
    queryFn: () => listUsers(params),
    placeholderData: prev => prev
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: ['adminUser', id],
    queryFn: () => fetchUser(id!),
    enabled: Boolean(id)
  });
}

export function useSuperCreatorPublishers(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['superCreatorPublishers', id],
    queryFn: () => listSuperCreatorPublishers(id!),
    enabled: enabled && Boolean(id)
  });
}

function apiErrMsg(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { errors?: { msg?: string } } } })?.response
      ?.data?.errors?.msg || fallback
  );
}

function invalidateUser(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: ['adminUser', id] });
  qc.invalidateQueries({ queryKey: ['adminUsers'] });
}

/** Per-user lifecycle action keyed by id (approve / reject / set-primary). */
function useUserAction(
  fn: (id: string) => Promise<AdminUser>,
  successMsg: string
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fn(id),
    onSuccess: (_data, id) => {
      invalidateUser(qc, id);
      toast.success(successMsg);
    },
    onError: (err: unknown) => toast.error(apiErrMsg(err, 'Action failed — try again.'))
  });
}

export function useApproveUser() {
  return useUserAction(approveUser, 'Account approved');
}

export function useRejectUser() {
  return useUserAction(rejectUser, 'Account rejected');
}

export function useSetPrimaryUser() {
  return useUserAction(setPrimaryUser, 'Set as primary');
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateUserInput }) =>
      updateUser(id, patch),
    onSuccess: (_data, vars) => {
      invalidateUser(qc, vars.id);
      toast.success('Account updated');
    },
    onError: (err: unknown) => toast.error(apiErrMsg(err, 'Update failed — try again.'))
  });
}
